import { authFetch, clearToken } from "../utils/auth";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Target, Users, BookOpen, BarChart3, Settings,
  TrendingUp, Clock, Dumbbell, AlertTriangle
} from "lucide-react";
import { motion } from "framer-motion";

const API = window.location.origin;

const SKILL_LABELS = {
  putting: { label: "Putting", color: "bg-emerald-500" },
  chipping: { label: "Chipping", color: "bg-blue-500" },
  pitching: { label: "Pitching", color: "bg-purple-500" },
  irons: { label: "Irons", color: "bg-amber-500" },
  driving: { label: "Driving", color: "bg-red-500" },
  course: { label: "Course", color: "bg-cyan-500" }
};

export default function CoachAnalytics() {
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchAnalytics(); }, []);

  const fetchAnalytics = async () => {
    try {
      const res = await authFetch(`${API}/api/coach/analytics`);
      if (res.ok) {
        setAnalytics(await res.json());
      } else if (res.status === 403) {
        navigate("/dashboard");
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const skillBreakdown = analytics?.skill_breakdown || {};
  const totalSessions = Object.values(skillBreakdown).reduce((a, b) => a + b, 0);
  const sortedSkills = Object.entries(skillBreakdown).sort((a, b) => b[1] - a[1]);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="px-6 py-4 border-b border-gray-200">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-500 flex items-center justify-center">
              <Target className="w-5 h-5 text-[#111]" />
            </div>
            <div>
              <span className="font-bold text-lg text-[#111]">GolfFlowPro</span>
              <Badge className="ml-2 bg-purple-500/20 text-purple-400 border-purple-500/30">Coach</Badge>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={() => navigate("/coach/settings")} className="text-[#555] hover:text-[#111]">
            <Settings className="w-5 h-5" />
          </Button>
        </div>
      </header>

      {/* Nav */}
      <nav className="px-6 py-3 border-b border-gray-200 bg-gray-50">
        <div className="max-w-6xl mx-auto flex gap-1">
          {[
            { id: "dashboard", label: "Dashboard", icon: <Target className="w-4 h-4" />, path: "/coach" },
            { id: "students", label: "Students", icon: <Users className="w-4 h-4" />, path: "/coach/students" },
            { id: "library", label: "Drill Library", icon: <BookOpen className="w-4 h-4" />, path: "/coach/library" },
            { id: "analytics", label: "Analytics", icon: <BarChart3 className="w-4 h-4" />, path: "/coach/analytics" }
          ].map((tab) => (
            <Button key={tab.id} variant="ghost" onClick={() => navigate(tab.path)}
              className={`flex items-center gap-2 ${tab.id === "analytics" ? "bg-gray-200 text-[#111]" : "text-[#555] hover:text-[#111]"}`}>
              {tab.icon} {tab.label}
            </Button>
          ))}
        </div>
      </nav>

      <main className="px-6 py-8">
        <div className="max-w-6xl mx-auto space-y-6">
          <div>
            <h1 className="text-2xl font-bold text-[#111]">Analytics</h1>
            <p className="text-[#555] text-sm">Squad performance insights over the last 30 days</p>
          </div>

          {/* Top Metrics */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: "Squad Compliance", value: `${analytics?.avg_squad_compliance || 0}%`, icon: <TrendingUp className="w-5 h-5 text-emerald-700" />, bg: "bg-emerald-500/20" },
              { label: "Total Hours (30d)", value: analytics?.total_practice_hours_month || 0, icon: <Clock className="w-5 h-5 text-blue-400" />, bg: "bg-blue-500/20" },
              { label: "Students", value: analytics?.student_count || 0, icon: <Users className="w-5 h-5 text-purple-400" />, bg: "bg-purple-500/20" },
              { label: "Weakest Skill", value: analytics?.weakest_skill ? analytics.weakest_skill.charAt(0).toUpperCase() + analytics.weakest_skill.slice(1) : "N/A", icon: <AlertTriangle className="w-5 h-5 text-[#111]" />, bg: "bg-amber-500/20" }
            ].map((m, i) => (
              <motion.div key={m.label} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
                <Card className="bg-white border-gray-200">
                  <CardContent className="p-5">
                    <div className={`w-10 h-10 rounded-xl ${m.bg} flex items-center justify-center mb-3`}>{m.icon}</div>
                    <p className="text-2xl font-bold text-[#111]">{m.value}</p>
                    <p className="text-[#555] text-sm">{m.label}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Skill Distribution */}
            <Card className="bg-white border-gray-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-[#111] text-base">Skill Distribution</CardTitle>
                <p className="text-xs text-[#555]">{totalSessions} total sessions across your squad</p>
              </CardHeader>
              <CardContent className="space-y-4">
                {sortedSkills.map(([skill, count]) => {
                  const pct = totalSessions > 0 ? Math.round((count / totalSessions) * 100) : 0;
                  const info = SKILL_LABELS[skill] || { label: skill, color: "bg-gray-500" };
                  return (
                    <div key={skill} className="space-y-1.5">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-[#555] capitalize">{info.label}</span>
                        <span className="text-sm font-medium text-[#111]">{count} ({pct}%)</span>
                      </div>
                      <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
                        <motion.div
                          className={`h-full rounded-full ${info.color}`}
                          initial={{ width: 0 }}
                          animate={{ width: `${pct}%` }}
                          transition={{ duration: 0.6, delay: 0.2 }}
                        />
                      </div>
                    </div>
                  );
                })}
                {sortedSkills.length === 0 && (
                  <p className="text-[#555] text-sm text-center py-6">No data yet. Students need to complete sessions.</p>
                )}
              </CardContent>
            </Card>

            {/* Key Insights */}
            <Card className="bg-white border-gray-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-[#111] text-base">Key Insights</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {analytics?.most_assigned_drill && (
                  <div className="p-4 rounded-lg bg-gray-100">
                    <div className="flex items-center gap-2 mb-1">
                      <Dumbbell className="w-4 h-4 text-emerald-700" />
                      <span className="text-sm font-medium text-[#111]">Most Assigned Drill</span>
                    </div>
                    <p className="text-[#555] text-sm">{analytics.most_assigned_drill}</p>
                  </div>
                )}

                <div className="p-4 rounded-lg bg-gray-100">
                  <div className="flex items-center gap-2 mb-1">
                    <TrendingUp className="w-4 h-4 text-blue-400" />
                    <span className="text-sm font-medium text-[#111]">Avg Compliance</span>
                  </div>
                  <p className="text-[#555] text-sm">
                    {(analytics?.avg_squad_compliance || 0) >= 75
                      ? "Your squad is performing well. Keep up the great work!"
                      : (analytics?.avg_squad_compliance || 0) >= 50
                      ? "Decent compliance. Consider checking in with students who are falling behind."
                      : "Low compliance detected. Reach out to your students and see what's holding them back."}
                  </p>
                </div>

                {analytics?.weakest_skill && (
                  <div className="p-4 rounded-lg bg-gray-100">
                    <div className="flex items-center gap-2 mb-1">
                      <AlertTriangle className="w-4 h-4 text-[#111]" />
                      <span className="text-sm font-medium text-[#111]">Focus Area Suggestion</span>
                    </div>
                    <p className="text-[#555] text-sm">
                      <span className="capitalize font-medium text-[#111]">{analytics.weakest_skill}</span> is the least practiced skill.
                      Consider assigning more drills in this area.
                    </p>
                  </div>
                )}

                {(!analytics?.most_assigned_drill && !analytics?.weakest_skill) && (
                  <p className="text-[#555] text-sm text-center py-6">Insights will appear once students start practicing.</p>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
}
